/**
 * Asset Cache Initialization
 *
 * Entry point for initializing the asset caching system.
 *
 * @module @shopkit/asset-cache
 */

import type { AssetCacheConfig } from "./config";

/**
 * Initialize the asset caching module
 *
 * Sets up the AssetLoader, VersionManager, and optionally registers
 * a Service Worker for offline asset caching.
 *
 * @param config - Asset cache configuration options
 * @returns Promise that resolves when initialization is complete
 *
 * @example
 * ```typescript
 * import { initAssetCache } from '@shopkit/asset-cache';
 *
 * // Initialize with default settings
 * await initAssetCache();
 *
 * // Initialize with custom settings
 * await initAssetCache({
 *   enabled: true,
 *   version: '1.0.0',
 *   storageName: 'my-store-assets',
 *   maxSize: 50, // MB
 *   maxAge: 86400, // 24 hours
 *   serviceWorkerEnabled: true,
 *   serviceWorkerPath: '/sw.js',
 * });
 * ```
 */
export async function initAssetCache(config?: AssetCacheConfig): Promise<void> {
  const { AssetLoader } = await import("./core/asset-loader");
  const { VersionManager } = await import("./core/version-manager");
  const { registerServiceWorker } = await import("./core/register-sw");
  const { mergeAssetCacheConfig } = await import("./config");

  // Merge configuration with defaults
  const mergedConfig = mergeAssetCacheConfig(config);

  // Initialize the asset loader with merged config
  await AssetLoader.init({
    cacheName: mergedConfig.storageName,
    enableServiceWorker: mergedConfig.serviceWorkerEnabled,
    debug: mergedConfig.debug,
    defaultTTL: mergedConfig.maxAge,
    maxSize: mergedConfig.maxSize * 1024 * 1024, // Convert MB to bytes
  });

  // Initialize the version manager
  VersionManager.init(mergedConfig.version);

  // Register the service worker if enabled
  if (mergedConfig.serviceWorkerEnabled) {
    await registerServiceWorker(mergedConfig.serviceWorkerPath);
  }
}
